import {
  EDataBaseType,
  ESdChangeReason,
  IActionDefinitionDto,
  IColumnTypes,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IDataDiagramSchemeDto,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance, Rule, RuleObject } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import ObjectHash from "object-hash";
import * as React from "react";
import { KeyboardEvent, ReactNode } from "react";
import { connect } from "react-redux";
import LocalStorageService from "../../../auth/local-storage-service";
import { DataDiagramSchemeService } from "../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../services/datasource-service";
import { OfflineTablesService } from "../../../services/offline-tables-service";
import { OpenAIService } from "../../../services/openai-service";
import {
  IManagedDbForeignKey,
  IManagedDbInboundForeignKey,
  TableEditorService
} from "../../../services/table-editor-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import BooleanSvg from "../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../svgs/parameter-type-icons/double.svg";
import FileContentSvg from "../../../svgs/parameter-type-icons/file-content.svg";
import GuidSvg from "../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../svgs/parameter-type-icons/int64.svg";
import LocationSvg from "../../../svgs/parameter-type-icons/location.svg";
import StringSvg from "../../../svgs/parameter-type-icons/string.svg";
import TableSvg from "../../../svgs/parameter-type-icons/table.svg";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { resolveTableEditorSqlDialect } from "../../table-editor/table-editor-dialect";
import {
  IInitialFilter,
  ITableNavigationTarget,
  ITableRowsEditorEmbedding,
  ITableRowsEditorHandle,
  ITableRowsPendingSummary,
  TableRowsEditor
} from "../../table-editor/table-rows-editor";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaRadioGroup, { KuikaRadioGroupOption } from "../../ui-kit/kuika-radio-group/kuika-radio-group";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { validateRenameField } from "../code-editor-shell/code-editor-rename";
import { IDataTypePanelCategory, IDataTypePanelOption } from "../shared/kuika-data-type-dropdown/kuika-data-type-panel";
import { TABLE_NAME_MESSAGE, TABLE_NAME_PATTERN, validateNewTableName } from "./datasource-create-actions";
import { getTableRenameNames, getTableRenameProblem } from "./datasource-rename";
import { requestDatasourceReveal } from "./datasource-reveal";
import { TableSwitcherChip } from "./editor-switcher/table-switcher-chip";
import {
  carryServerErrors,
  ColumnErrorMap,
  columnsNeedAttentionMessage,
  DUPLICATE_COLUMN_NAME_MESSAGE,
  editedColumnFields,
  IColumnRowErrors,
  mergeColumnErrors,
  validateColumnName,
  validateColumns
} from "./table-column-errors";
import { findColumnsInMigrationError, parseReservedColumnNames } from "./table-column-server-errors";
import {
  columnTypeName,
  groupColumnTypes,
  IColumnTypeGroup,
  isTableReferenceType,
  resolveColumnType
} from "./table-column-types";
import TableColumnsGrid, { IColumnRowValues, IProposedColumnType } from "./table-columns-grid";
import "./create-table.scss";
import "./editor-bar-surface.scss";
import {
  getDataTabDisabledReason,
  isSameTable,
  ITableEditorView,
  TABLE_EDITOR_TABS,
  TableEditorTab
} from "./table-editor-view";
import {
  buildColumnKeys,
  buildTableRelations,
  ITableColumnKey,
  ITableReference,
  ITableRelations
} from "./table-relations";

/** How many relation chips a row shows before it summarises the rest. */
const RELATION_CHIP_LIMIT: number = 6;

enum EWorkOfflineType {
  WithSchema,
  WithData
}

/** What the managed-DB row query says about this table's keys. Empty until it answers. */
export interface ITableKeyData {
  primaryKeyColumns: string[];
  foreignKeys: IManagedDbForeignKey[];
  inboundForeignKeys: IManagedDbInboundForeignKey[];
}

/** What this editor knows about a table's keys before the query answers, and after it fails. */
const noKeyData = (): ITableKeyData => ({ primaryKeyColumns: [], foreignKeys: [], inboundForeignKeys: [] });

/** The rows before the Data tab has said anything — and whenever it has not been opened. */
const NO_PENDING_ROWS: ITableRowsPendingSummary = { isDirty: false, dirtyCount: 0, isSaving: false };

/** Shown on the Data tab while the columns are edited: the grid there is the table as it is saved. */
const SCHEMA_NOTICE: ReactNode = (
  <div className="kuika-table-editor__banner kuika-table-editor__banner--info" data-testid="tableEditorSchemaNotice">
    Rows reflect the saved table — your column changes apply after UPDATE
  </div>
);

/** Why the draft row holds UPDATE back: said around the row itself once an UPDATE has been refused over it. */
const DRAFT_PENDING_MESSAGE: string =
  "This column is not added yet — give it a valid name and a data type, or clear it";

/** What a failed save call hands back: the service catches the Axios error and returns it. */
interface IStatusBearer {
  status?: number;
}

/** The type picker's tabs, kept while the types and the project's schema hold. */
interface ITypeCategoriesCache {
  columnTypes: IColumnTypes[];
  projectSchema: string | undefined;
  categories: IDataTypePanelCategory[];
}

/** The errors the grid draws, kept while what they are derived from holds. */
interface IColumnErrorsCache {
  tempData: IDatabaseColumnModel[];
  columnTypes: IColumnTypes[];
  serverColumnErrors: ColumnErrorMap;
  isShown: boolean;
  errors: ColumnErrorMap;
}

/** What a tab request (Browse Data landing here) sets. */
interface ITableTabState {
  activeTab: TableEditorTab;
  hasOpenedData: boolean;
  dataFilter?: IInitialFilter;
}

/** How a commit was asked for. */
export interface ICreateTableCommitOptions {
  /** The close dialog has just saved the rows, so pending rows are no reason to refuse. */
  rowsSettled?: boolean;
}

export interface ICreateTableState {
  keyData: ITableKeyData;
  /** The grid's draft row has been started but is missing a name or a type. */
  hasPendingColumnDraft: boolean;
  isRelationsExpanded: boolean;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  columnTypes: IColumnTypes[];
  tempData: IDatabaseColumnModel[];
  currentColType: string;
  currentRequired: boolean;
  deletedColumns: IDatabaseColumnModel[];
  searchText: string;
  submitButtonDisabledStatus: boolean;
  tableColumnSizeType: ETableColumnSizeType;
  shouldGenerateColumnWithAI: boolean;
  isColumnGeneratingWithAI: boolean;
  isNameOnEdit: boolean;
  datasourceName: string;
  isWorkOffline: boolean;
  workOfflineType: EWorkOfflineType;
  enableEditing: boolean;
  isDataLossConfirmOpen: boolean;
  /** Columns is the schema editor; Data embeds the row browser. */
  activeTab: TableEditorTab;
  /** Data mounts on its first visit and then stays, so its pending rows survive a trip to Columns. */
  hasOpenedData: boolean;
  /** The filter the Data tab opened on — an FK jump lands on the referenced row. */
  dataFilter?: IInitialFilter;
  /** What the Data tab holds unsaved, for its badge and the close dialog. */
  rowsPending: ITableRowsPendingSummary;
  /**
   * What the server refused, by the row's object — a reserved name, a migration that failed on a
   * column. The local checks are derived from the rows on every read and never stored.
   */
  serverColumnErrors: ColumnErrorMap;
  /**
   * How many times an UPDATE has been refused over the columns. Zero draws no local error: a grid
   * does not turn red while its first row is being typed. Each refusal takes the reader to the
   * first marked row.
   */
  columnErrorFocusRequest: number;
}

export interface ICreateTableProps {
  tableData: IDatabaseTableModel;
  datasource?: IDatasourceDto;
  /** A tab request from the provider (Browse Data); acted on once per `requestId`. */
  view?: ITableEditorView;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

enum ETableColumnSizeType {
  Custom = "custom",
  Max = "max"
}

/** The fixed default-value choices a column type offers. */
const BOOLEAN_DEFAULT_OPTIONS: KuikaSelectboxOption[] = [
  { value: "true", label: "True" },
  { value: "false", label: "False" }
];

const DATETIME_OFFSET_DEFAULT_OPTIONS: KuikaSelectboxOption[] = [{ value: "Now", label: "Now" }];

const DATETIME_DEFAULT_OPTIONS: KuikaSelectboxOption[] = [{ value: "GetDate()", label: "Get Date" }];

class CreateTable extends PureSdComponent<ICreateTableProps & IWithSDProps, ICreateTableState> {
  private readonly isSqlServerWorkspace: boolean = false;

  formRef = React.createRef<FormInstance>();

  colNameRef = React.createRef<HTMLInputElement>();

  prevNewColName: string = "";

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  // The in-flight key query. The managed-DB schema call takes ~2.3s, so opening a second table
  // before the first answers is routine, and without this the slower response lands last and
  // writes one table's keys under another's name. Same guard the rows grid uses.
  keysAbort: AbortController | null = null;

  rowsEditorRef = React.createRef<ITableRowsEditorHandle>();

  /** The last tab request acted on. A request is acted on once; the tab is the user's after that. */
  handledViewRequestId: number | undefined = undefined;

  /**
   * SAVE & CLOSE is running. Every update re-runs the guarded close while a navigation is pending, and after an
   * `await` each dispatch renders at once (React 17): mid-flow that would ask again, or close the editor a second
   * time after SAVE & CLOSE closed it. The flow closes (or stays) itself.
   */
  isSavingForClose: boolean = false;

  /**
   * The rows' pending state as of the latest commit — what the close guard reads. It is written from the rows
   * editor's layout effect, so it is current in the same commit as, say, a discard confirmed just before an FK
   * jump; the state copy is for rendering and lands a render later.
   */
  liveRowsPending: ITableRowsPendingSummary = NO_PENDING_ROWS;

  /** The embedding handed to the rows editor, kept while its inputs hold so a Columns re-render skips the grid. */
  rowsEmbedding: ITableRowsEditorEmbedding | undefined = undefined;

  /**
   * The saved column each edited row descends from. An edit replaces a row's object, and neither
   * an id (it can be null) nor a name (it is what was edited) leads back to the saved one — the
   * chain of objects does. The checks run on UPDATE use it to leave untouched saved values alone.
   */
  columnOrigins: WeakMap<IDatabaseColumnModel, IDatabaseColumnModel> = new WeakMap<
    IDatabaseColumnModel,
    IDatabaseColumnModel
  >();

  typeCategoriesCache: ITypeCategoriesCache | undefined = undefined;

  columnErrorsCache: IColumnErrorsCache | undefined = undefined;

  constructor(props: ICreateTableProps & IWithSDProps) {
    super(props);
    // Opened for Browse Data: the first render is already on Data.
    this.handledViewRequestId = props.view?.requestId;
    const initialTab: ITableTabState = this.tabStateFor(props.view, props, "columns", false, undefined) ?? {
      activeTab: "columns",
      hasOpenedData: false
    };
    this.state = {
      saveStatus: "idle",
      columnTypes: [],
      tempData: [],
      currentColType: "",
      currentRequired: false,
      deletedColumns: [],
      searchText: "",
      submitButtonDisabledStatus: true,
      tableColumnSizeType: ETableColumnSizeType.Custom,
      keyData: noKeyData(),
      hasPendingColumnDraft: false,
      isRelationsExpanded: false,
      shouldGenerateColumnWithAI: false,
      isColumnGeneratingWithAI: false,
      isNameOnEdit: false,
      datasourceName: "",
      isWorkOffline: false,
      workOfflineType: EWorkOfflineType.WithSchema,
      enableEditing: false,
      isDataLossConfirmOpen: false,
      activeTab: initialTab.activeTab,
      hasOpenedData: initialTab.hasOpenedData,
      dataFilter: initialTab.dataFilter,
      rowsPending: NO_PENDING_ROWS,
      serverColumnErrors: new Map<IDatabaseColumnModel, IColumnRowErrors>(),
      columnErrorFocusRequest: 0
    };

    this.isSqlServerWorkspace = LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.MSSQL;
  }

  // UNSAFE_componentWillReceiveProps = (nextProps) => {
  //   if (nextProps && nextProps.tableData && nextProps.tableData.columns) {
  //     this.setState({
  //       tempData: nextProps.tableData.columns,
  //       tableName: nextProps.tableData.name
  //     });
  //   }
  // };

  componentDidMount() {
    this.getColumnTypes();
    this.loadTableKeys();
    const fieldsValue = { ...this.formRef.current?.getFieldsValue() };
    const columns = this.props.tableData?.columns ?? [];
    if (!columns || columns?.length === 0) {
      const obj = {} as IDatabaseColumnModel;
      obj.id = Guid.create().toString();
      obj.name = "Id";
      obj.sqlDataType = "uniqueidentifier";
      obj.kuikaDataType = "Guid";
      obj.maxLength = null;
      obj.isPk = true;
      obj.isUnique = false;
      obj.defaultValue = null;
      obj.nullable = false;
      obj.dropped = false;
      obj.decimalScale = null;

      columns?.push(obj);
    }
    let state = _.cloneDeep(this.state);
    if (this.props.tableData) {
      const offlineTableData = this.props.sd.offlineTables.find((ot) => ot.tableName === this.props.tableData.name);
      let isWorkOffline = false;
      let workOfflineType = EWorkOfflineType.WithSchema;
      let enableEditing = false;

      if (offlineTableData) {
        isWorkOffline = true;
        workOfflineType = offlineTableData.withData ? EWorkOfflineType.WithData : EWorkOfflineType.WithSchema;
        fieldsValue.whereCriteria = offlineTableData.whereCriteria;
        fieldsValue.willSendRecordsCriteria = offlineTableData.willSendRecordsCriteria;
        fieldsValue.enableEditing = offlineTableData.enableEditing;
        enableEditing = fieldsValue.enableEditing;
      }

      state = {
        ...state,
        enableEditing,
        tempData: columns,
        datasourceName: this.props.tableData.name,
        isWorkOffline,
        workOfflineType
      };
      fieldsValue.tableName = this.props.tableData.name;
    } else {
      // A new table opens on its generated name (`Table<n>`) with the name field already open: it is
      // named here, in the bar, as a new action is. The field mounts on this update, after the value
      // below is in the form, so it focuses with the whole name selected. The hash is taken on the
      // generated name, so a table nobody touched still closes without asking.
      state = {
        ...state,
        tempData: columns,
        datasourceName: this.props?.sd?.activeCreatingTableName ?? "",
        isNameOnEdit: true
      };
      fieldsValue.tableName = this.props?.sd?.activeCreatingTableName ?? "";
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.objectHash = this.getObjectHash(state, fieldsValue);
    this.setState(state);
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.keysAbort?.abort();
  }

  componentDidUpdate(prevProps: ICreateTableProps & IWithSDProps) {
    const isAnotherTable: boolean = prevProps.tableData?.name !== this.props.tableData?.name;
    if (isAnotherTable) {
      this.loadTableKeys();
    }
    let state = _.cloneDeep(this.state);
    if (isAnotherTable) {
      // Drop the outgoing table's keys as the name changes: they are not this table's, and the
      // query that replaces them is seconds away. This has to ride the snapshot below rather
      // than a setState of its own — the snapshot is read from this.state and committed
      // wholesale, so anything set separately in this tick is written back over.
      // What was said about the outgoing table's columns goes with it.
      state = {
        ...state,
        keyData: noKeyData(),
        isRelationsExpanded: false,
        serverColumnErrors: new Map<IDatabaseColumnModel, IColumnRowErrors>(),
        columnErrorFocusRequest: 0
      };
    }
    const fieldsValue = { ...this.formRef.current?.getFieldsValue() };
    const tabRequest: ITableTabState | null = this.takeTabRequest();
    if (prevProps.tableData != this.props.tableData) {
      let isWorkOffline = false;
      let workOfflineType = EWorkOfflineType.WithSchema;
      if (!this.props.tableData) {
        state = { ...state, tempData: [], shouldGenerateColumnWithAI: false, isWorkOffline, workOfflineType };
        fieldsValue.tableName = "";
        fieldsValue.whereCriteria = "";
        fieldsValue.willSendRecordsCriteria = "";
        fieldsValue.enableEditing = false;
      } else {
        const offlineTableData = this.props.sd.offlineTables.find((ot) => ot.tableName === this.props.tableData.name);

        if (offlineTableData) {
          isWorkOffline = true;
          workOfflineType = offlineTableData.withData ? EWorkOfflineType.WithData : EWorkOfflineType.WithSchema;
          fieldsValue.whereCriteria = offlineTableData.whereCriteria;
          fieldsValue.willSendRecordsCriteria = offlineTableData.willSendRecordsCriteria;
          fieldsValue.enableEditing = offlineTableData.enableEditing;
        }
        state = {
          ...state,
          tempData: this.props.tableData?.columns,
          datasourceName: this.props.tableData.name,
          isWorkOffline,
          workOfflineType
        };
        fieldsValue.tableName = this.props.tableData.name;
      }
      // A tab request that came with the new table rides the same snapshot, for the reason above.
      if (tabRequest) state = { ...state, ...tabRequest };

      this.formRef.current?.setFieldsValue(fieldsValue);
      this.objectHash = this.getObjectHash(state, fieldsValue);
      this.setState(state);
      return;
    }
    if (tabRequest) this.setState(tabRequest);

    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    // SAVE & CLOSE saving the rows first is the same: its own close follows the save.
    if (this.props.nextCallback && this.saveStatus.canStart && !this.isSavingForClose) {
      this.handleCloseDatasource();
    }
  }

  /** Why Data is off for this editor, or undefined when it is on. */
  getDataTabDisabledReason(props: ICreateTableProps = this.props): string | undefined {
    return getDataTabDisabledReason({ isCreating: !props.tableData, datasource: props.datasource });
  }

  /**
   * What a tab request sets, from the tab state it finds. Null when there is nothing to do: no request, or Data
   * asked for where it is off. A request without a filter keeps the one the Data tab is on — re-keying the rows
   * would drop their pending edits without a word.
   */
  tabStateFor(
    view: ITableEditorView | undefined,
    props: ICreateTableProps,
    activeTab: TableEditorTab,
    hasOpenedData: boolean,
    dataFilter: IInitialFilter | undefined
  ): ITableTabState | null {
    if (!view) return null;
    if (view.tab === "columns") return { activeTab: "columns", hasOpenedData, dataFilter };
    if (this.getDataTabDisabledReason(props)) return null;
    return { activeTab: "data", hasOpenedData: true, dataFilter: view.initialFilter ?? dataFilter };
  }

  /** The tab a NEW request asks for — each `requestId` once. */
  takeTabRequest = (): ITableTabState | null => {
    const view: ITableEditorView | undefined = this.props.view;
    if (!view || view.requestId === this.handledViewRequestId) return null;
    this.handledViewRequestId = view.requestId;
    return this.tabStateFor(view, this.props, this.state.activeTab, this.state.hasOpenedData, this.state.dataFilter);
  };

  /** A tab click. It never asks anything: both tabs stay mounted, so nothing is lost by switching. */
  handleTabChange = (value: string): void => {
    const activeTab: TableEditorTab = value === "data" ? "data" : "columns";
    if (activeTab === "data" && this.getDataTabDisabledReason()) return;
    this.setState({ activeTab, hasOpenedData: this.state.hasOpenedData || activeTab === "data" });
  };

  /** Browse Data on the table that is open: only the tab changes. */
  openDataTab = (): void => this.handleTabChange("data");

  /** The rows editor's report. The guard reads the live copy; the state copy re-renders the tab's badge. */
  handleRowsPendingChange = (summary: ITableRowsPendingSummary): void => {
    this.liveRowsPending = summary;
    const current: ITableRowsPendingSummary = this.state.rowsPending;
    if (
      current.isDirty === summary.isDirty &&
      current.dirtyCount === summary.dirtyCount &&
      current.isSaving === summary.isSaving
    ) {
      return;
    }
    this.setState({ rowsPending: summary });
  };

  /**
   * An FK "go to" from the rows. Into this table: the Data tab re-opens on the referenced row (the rows editor's
   * own discard guard has already run). Into another: that table's Browse Data, parked as this editor's pending
   * close so its guard — columns included — runs first, once the prop has arrived.
   */
  handleRowsNavigate = (target: ITableNavigationTarget, filter: IInitialFilter): void => {
    const tableData: IDatabaseTableModel | undefined = this.props.tableData;
    if (tableData && isSameTable(tableData, target)) {
      this.setState({ dataFilter: filter, activeTab: "data", hasOpenedData: true });
      return;
    }
    const datasource: IDatasourceDto | undefined = this.props.datasource;
    if (!datasource) return;
    this.props.setNextCallbackFunction((): void =>
      this.props.sd.setBrowsingRowsTable(datasource, {
        name: target.name,
        schema: target.schema,
        _initialFilter: filter
      })
    );
  };

  getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return <StringSvg />;
      case "boolean":
        return <BooleanSvg />;
      case "bytea":
      case "bytea":
        return <DoubleSvg />;
      case "byte":
        return <DoubleSvg />;
      case "date":
        return <DateSvg />;
      case "datetime":
        return <DateTimeSvg />;
      case "location":
        return <LocationSvg />;
      case "filecontent":
        return <FileContentSvg />;
      case "datetimeoffset":
      case "timestamp with time zone":
        return <DateTimeOffsetSvg />;
      case "decimal":
      case "numeric":
        return <DecimalSvg />;
      case "double":
        return <DoubleSvg />;
      case "float":
        return <DoubleSvg />;
      case "varbinary":
        return <DoubleSvg />;
      case "guid":
        return <GuidSvg />;
      case "int16":
        return <Int16Svg />;
      case "int32":
        return <Int32Svg />;
      case "int64":
        return <Int64Svg />;
      case "single":
      case "bytea":
      case "binary":
      case "double precision":
        return <DoubleSvg />;
      case "string":
        return <StringSvg />;
      case "image":
        return <ImageSvg />;
      case "int":
      case "integer":
        return <Int32Svg />;
      case "bigint":
      case "bigint identity(1,1) not":
      case "autoincrement":
        return <Int64Svg />;
      case "smallint":
        return <Int16Svg />;
      case "nvarchar":
      case "character varying":
        return <StringSvg />;
      case "uniqueidentifier":
      case "uuid":
        return <GuidSvg />;
      case "bit":
        return <BooleanSvg />;
      default:
        const isTable = this.props.sd.schemas
          .get(this.props.datasource?.id ?? "")
          ?.tables.find((table) => table.name === type || type === `${table.schema}.${table.name}`);

        if (isTable) {
          return <TableSvg />;
        }
        return <span style={{ width: 20, height: 20 }}></span>;
    }
  };

  onCreate = async (options?: ICreateTableCommitOptions): Promise<void> => {
    if (!this.props.datasource?.id) return;
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;

    // UPDATE commits the schema and closes the editor; pending rows would close with it, unsaved.
    if (this.liveRowsPending.isDirty && !options?.rowsSettled) {
      KMainFunctions.displayWarningNotification(
        "Save or discard the row changes on the Data tab first — UPDATE closes the editor."
      );
      return;
    }

    try {
      this.saveStatus.start();
      // A name still being typed is confirmed first, by the field's own rules; refused, it says why
      // under the field and nothing is sent.
      const tableName: string | undefined = await this.resolveNameForSave();
      if (tableName === undefined) {
        this.saveStatus.reset();
        return;
      }
      const tableSchema = this.props.tableData?.schema
        ? this.props.tableData.schema
        : this.props.sd.projects.find((project) => project.id === this.props.sd.project.id)?.schemaName;
      const withData =
        this.state.workOfflineType === EWorkOfflineType.WithData ||
        (this.state.isWorkOffline && this.props.sd.isEditingDefaultKuikaTable) ||
        false;
      let whereCriteria = this.formRef.current?.getFieldValue("whereCriteria")?.trim();
      let willSendRecordsCriteria = this.formRef.current?.getFieldValue("willSendRecordsCriteria")?.trim();
      let enableEditing = this.state.enableEditing;

      if (!tableName) {
        KMainFunctions.displayWarningNotification("Please enter a name for the table");
        this.saveStatus.reset();
        return;
      }

      // Everything the grid can tell without the server, all at once: each offending row and cell
      // is marked and the first one brought into view, under ONE toast — a toast per problem said
      // nothing about where any of them was. The draft row lives in the grid, so the save cannot
      // collect it; refusing is the honest answer, since going ahead dropped it without a word.
      const localErrors: ColumnErrorMap = validateColumns(
        this.state.tempData ?? [],
        this.savedColumnOf,
        this.state.columnTypes ?? []
      );
      const rowsNeedingAttention: number = localErrors.size + (this.state.hasPendingColumnDraft ? 1 : 0);
      if (rowsNeedingAttention > 0) {
        KMainFunctions.displayWarningNotification(columnsNeedAttentionMessage(rowsNeedingAttention));
        this.setState({ columnErrorFocusRequest: this.state.columnErrorFocusRequest + 1 });
        this.saveStatus.reset();
        return;
      }

      if (this.state.isWorkOffline && withData && whereCriteria) {
        const testQuery = await DatasourceService.ExecuteQuery({
          kDatasource_Id: this.props.datasource.id,
          statement: `Select * FROM ${this.getTableSchemaName()}.${tableName} WHERE ${whereCriteria}`,
          actionParameters: [
            { name: "kuserid", Typename: "String" },
            { name: "kusername", Typename: "String" }
          ]
        } as IActionDefinitionDto);

        if (testQuery.status !== 200) return;
      }

      if (this.state.tempData && this.state.tempData.length > 0) {
        const isPropsExist = _.isEmpty(this.props.tableData);
        const final = {} as IDataDiagramSchemeDto;

        if (isPropsExist) {
          const currentState = [...this.state.tempData];

          const tableObj = {} as IDatabaseTableModel;
          tableObj.id = Guid.create().toString();
          tableObj.name = tableName;
          tableObj.schema = tableSchema as string;
          tableObj.dropped = false;
          tableObj.columns = currentState;

          final.tables = [tableObj];
        } else {
          const newState = [...this.state.tempData];

          if (this.state.deletedColumns.length > 0) {
            const deletedItems = [...this.state.deletedColumns];
            deletedItems.map((item) => {
              item.dropped = true;
            });
            deletedItems.forEach((element) => {
              newState.push(element);
            });
          }

          const tableObj = {} as IDatabaseTableModel;
          tableObj.id = this.props.tableData.id;
          tableObj.name = tableName;
          tableObj.schema = tableSchema as string;
          tableObj.dropped = this.props.tableData.dropped;
          tableObj.columns = newState;

          final.tables = [tableObj];
        }

        // What the server said last time is about to be asked again, on what is being sent now.
        this.setState({ serverColumnErrors: new Map<IDatabaseColumnModel, IColumnRowErrors>() });

        // The reserved-key check used to run once per added column; it validates the whole
        // schema, so it belongs here — once, on what is actually about to be written.
        const reservedKeyCheck = await DataDiagramSchemeService.CheckSchemaReservedKeyUsed(final);
        if (!reservedKeyCheck || reservedKeyCheck.status !== 200) {
          // The service has already said it in a toast; the rows it names are marked as well.
          this.markServerColumnErrors(
            parseReservedColumnNames(KMainFunctions.getApiErrorMessage(reservedKeyCheck)),
            (name: string): IColumnRowErrors => ({
              fields: { name: `${name} is a reserved word. Choose another name.` }
            })
          );
          this.saveStatus.fail();
          return;
        }

        const promises = [
          !this.props.sd.isEditingDefaultKuikaTable
            ? DataDiagramSchemeService.ExecuteMigrationScript(final)
            : Promise.resolve({ status: 200 }),
          this.state.isWorkOffline
            ? OfflineTablesService.save({
                tableName,
                withData,
                whereCriteria,
                willSendRecordsCriteria,
                enableEditing
              })
            : Promise.resolve({ status: 200 }),
          (this.props.tableData && this.props.tableData.name !== tableName) ||
          (this.props.sd.offlineTables.find((ot) => ot.tableName === tableName) && !this.state.isWorkOffline)
            ? OfflineTablesService.delete(tableName)
            : Promise.resolve({ status: 200 })
        ];

        const responses = await Promise.allSettled(promises);
        if (responses[0].status === "rejected") throw responses[0].reason;
        if (responses[1].status === "rejected") throw responses[1].reason;
        if (responses[2].status === "rejected") throw responses[2].reason;
        const dataDiagramReq: IStatusBearer = responses[0].value as IStatusBearer;
        if (dataDiagramReq.status !== 200) {
          // The provider's own text, already toasted by the service. Where it names a column, the
          // row is outlined with it; text that names none leaves the toast to say it alone.
          const message: string | undefined = KMainFunctions.getApiErrorMessage(dataDiagramReq);
          this.markServerColumnErrors(
            findColumnsInMigrationError(
              message,
              (this.state.tempData ?? []).map((row: IDatabaseColumnModel): string => row.name)
            ),
            (): IColumnRowErrors => ({ row: message })
          );
          // A resolved response that is not 200: the `finally` reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshTables();
        if (isPropsExist) {
          this.props.sd.setLastAddedTable(tableName, this.props.datasource?.id);
        }
        this.props.sd.updateDatabaseDiagramData();
        // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          // The list behind the editor lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: tableName, parentId: this.props.datasource?.id });
          this.closeDataSource();
        });
      } else {
        KMainFunctions.displayWarningNotification("Please add a column!");
        this.saveStatus.reset();
      }
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  getTableSchemaName(): string {
    return (
      this.props.tableData?.schema ??
      this.props.sd.projects.find((project) => project.id === this.props.sd.project.id)?.schemaName ??
      ""
    );
  }

  getObjectHash(state?: ICreateTableState, formValues?: any): string {
    const { whereCriteria, willSendRecordsCriteria, enableEditing } =
      formValues || this.formRef.current?.getFieldsValue();
    const { tempData, workOfflineType, isWorkOffline, datasourceName } = state || this.state;

    return ObjectHash({
      // The editor's own name, not the field's. The rename field only exists while a rename
      // is open, and `getFieldsValue()` reports registered fields only — so reading the name
      // from there made an untouched table look edited the moment the field was not mounted.
      tableName: datasourceName,
      tempData,
      whereCriteria: whereCriteria || "",
      willSendRecordsCriteria,
      workOfflineType,
      isWorkOffline,
      enableEditing
    });
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
    this.props.sd.clearCSharpTempValues();
    this.props.sd.updateDatabaseDiagramData();
  }

  /** The schema side has unsaved changes. The form is read only once it is mounted. */
  isColumnsDirty = (): boolean => !!this.formRef.current && this.objectHash !== this.getObjectHash();

  /** The guarded close: it covers the columns and the rows alike. */
  handleCloseDatasource() {
    if (!this.isColumnsDirty() && !this.liveRowsPending.isDirty) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  /**
   * The close dialog's SAVE & CLOSE. Rows first — they are saved by their own endpoint, and a failed batch is said
   * on the Data tab, so the editor stays open there and the navigation that asked is dropped. Then the columns,
   * whose commit closes the editor on success; with nothing to commit there, it closes here.
   */
  saveAndClose = async (): Promise<void> => {
    this.isSavingForClose = true;
    try {
      if (this.liveRowsPending.isDirty) {
        const saved: boolean = (await this.rowsEditorRef.current?.saveAll()) ?? false;
        if (!saved) {
          // The navigation goes before the dialog does: hiding the dialog renders, and a navigation still parked
          // then would be asked about again.
          this.props.setNextCallbackFunction();
          this.props.setShowModal(false);
          this.setState({ activeTab: "data", hasOpenedData: true });
          return;
        }
      }

      if (this.isColumnsDirty()) {
        await this.onCreate({ rowsSettled: true });
        return;
      }

      this.props.setShowModal(false);
      this.closeDataSource();
    } finally {
      this.isSavingForClose = false;
    }
  };

  getColumnTypes = async () => {
    try {
      const res = await DataDiagramSchemeService.GetColumnTypes();
      if (res && res.status === 200) {
        // The two date primitives are not offered for a new column; a table that happens to be
        // called Date is still a table.
        const columnTypes: IColumnTypes[] | undefined = res.data?.filter(
          (column: IColumnTypes): boolean =>
            isTableReferenceType(column) || (column.kuikaTypeName !== "Date" && column.kuikaTypeName !== "Datetime")
        );
        this.setState({ columnTypes });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  /** A column built from the draft row. The per-type shape the card's submit applied, inlined. */
  buildNewColumn = (values: IColumnRowValues): IDatabaseColumnModel => {
    const colTypeObj = JSON.parse(values.coltype) as IColumnTypes;
    const typeName: string = colTypeObj.kuikaTypeName;
    const sized: boolean = typeName === "String" || typeName === "Binary";

    const column = {} as IDatabaseColumnModel;
    column.id = Guid.create().toString();
    column.name = values.colname;
    column.kuikaDataType = columnTypeName(colTypeObj);
    column.sqlDataType = colTypeObj.dbTypeName;
    // Binary is varbinary(MAX) unless a width is asked for; String starts on the card's 400.
    if (sized) column.maxLength = values.defaultSize ?? (typeName === "Binary" ? -1 : 400);
    else column.maxLength = colTypeObj.maxLength;
    column.decimalScale = (
      typeName === "Decimal" ? String(values.decimalScale ?? 5) : colTypeObj.decimalScale
    ) as IDatabaseColumnModel["decimalScale"];
    column.nullable = !values.required;
    column.isUnique = values.unique;
    if (!values.required) column.defaultValue = null;
    else column.defaultValue = (values.defaultValue ?? this.defaultValueForType(typeName)) as string;
    return column;
  };

  /**
   * The draft row becomes a column. It lands in the list where it was typed and stays editable
   * there — no round-trip, no re-sort, nothing reset. The schema checks that used to run here
   * belong to CREATE: run per row they made the row appear late and out of place, and the user
   * had not finished describing it yet.
   */
  handleAddColumn = (values: IColumnRowValues): string | null => {
    if (!values.coltype) return DRAFT_PENDING_MESSAGE;
    const name: string = values.colname.trim();
    const error: string | null = this.validateNewColumnName(name, undefined);
    if (error) {
      KMainFunctions.displayWarningNotification(error);
      return error;
    }

    this.setState(
      { tempData: [...this.state.tempData, this.buildNewColumn({ ...values, colname: name })] },
      this.setSubmitButtonDisabledStatus
    );
    return null;
  };

  onFinishFailed = (errorInfo) => {};

  /**
   * What the column name suggests — the proposal the card's name field ran on blur and on
   * Enter: the AI when its toggle is on, the name-based guess otherwise. The grid owns the
   * draft row now, so a proposal comes back as a value instead of being written into the form.
   */
  proposeColumnType = async (colname: string, hasType: boolean): Promise<IProposedColumnType | null> => {
    // Nothing is picked for the user unless they asked for it. The sparkle is that ask: with it
    // off the type cell stays exactly as they left it, and the name-based guess below runs only
    // as the offline fallback for a request that could not be made.
    if (!this.state.shouldGenerateColumnWithAI) return null;

    // One proposal per name: re-proposing on every blur would keep undoing a type the user
    // picked over the guess.
    if (this.prevNewColName === colname) return null;
    this.prevNewColName = colname;

    return this.proposeColumnTypeWithAi(colname, hasType);
  };

  proposeColumnTypeWithAi = async (colname: string, hasType: boolean): Promise<IProposedColumnType | null> => {
    this.setState({ isColumnGeneratingWithAI: true });
    try {
      const {
        data: { columnType }
      } = await OpenAIService.GenerateColumnTypes(colname);
      const columnTypeInfo: string[] = columnType.split(";");
      const stringColumnType: IColumnTypes | undefined = this.state.columnTypes.find(
        (colType) => colType.kuikaTypeName === "String"
      );
      let kuikaColumnType: IColumnTypes | undefined = this.state.columnTypes.find(
        (colType) => colType.kuikaTypeName.toLowerCase() === columnTypeInfo[0].toLowerCase()
      );

      if (!kuikaColumnType) {
        if (!stringColumnType) {
          throw new Error("The AI generated column type does not found, please try another Column Name");
        }
        kuikaColumnType = stringColumnType;
      }

      this.setState({
        currentColType: kuikaColumnType.kuikaTypeName,
        tableColumnSizeType: ETableColumnSizeType.Custom
      });

      return {
        coltype: jsonStringifySafe(kuikaColumnType),
        defaultSize: columnTypeInfo[0] === "String" ? parseInt(columnTypeInfo[1], 10) : null
      };
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // The name did not produce a type, so the next blur should be free to ask again.
      this.prevNewColName = "";
      // The user asked for a suggestion and the service could not give one — the local
      // name rule is the fallback, not the default.
      return this.proposeColumnTypeFromName(colname, hasType);
    } finally {
      this.setState({ isColumnGeneratingWithAI: false });
    }
  };

  /** The type a column name gives itself away with: `*Name` is a string, `Is*` a boolean, and so on. */
  proposeColumnTypeFromName = (colname: string, hasType: boolean): IProposedColumnType | null => {
    const lowered: string = colname.toLowerCase();
    const typeByName = (kuikaTypeName: string): IColumnTypes | undefined =>
      this.state.columnTypes.find((type) => type.kuikaTypeName === kuikaTypeName);

    const propose = (type: IColumnTypes | undefined, defaultSize?: number): IProposedColumnType | null => {
      if (!type) return null;
      this.setState({ currentColType: type.kuikaTypeName });
      if (defaultSize != null) this.setState({ tableColumnSizeType: ETableColumnSizeType.Custom });
      return { coltype: jsonStringifySafe(type), defaultSize: defaultSize ?? null };
    };

    if (!hasType) {
      if (["name", "description"].some((x) => lowered.includes(x))) {
        return propose(typeByName("String"), lowered.includes("name") ? 200 : 400);
      }
      if (lowered.startsWith("is")) return propose(typeByName("Boolean"));
      if (lowered.includes("datetime")) return propose(typeByName("Datetime"));
      if (lowered.includes("date")) return propose(typeByName("Date"));
    }

    // `<Type>Id` names the type it points at, which holds whether or not one is already picked.
    if (lowered.endsWith("id")) {
      return propose(this.state.columnTypes.find((type) => lowered.startsWith(type.kuikaTypeName.toLowerCase())));
    }

    return null;
  };

  /**
   * Where a column sits in `tempData`. Identity first: the backend hands some columns back
   * with a null id, and matching those by id lands on whichever null-id row comes first.
   */
  columnIndex = (column: IDatabaseColumnModel): number => {
    const byRef: number = this.state.tempData.indexOf(column);
    if (byRef !== -1) return byRef;
    if (column.id) {
      const byId: number = this.state.tempData.findIndex((item) => item.id === column.id);
      if (byId !== -1) return byId;
    }
    return this.state.tempData.findIndex((item) => item.name === column.name);
  };

  /**
   * An edit made in a cell. It writes the row and nothing else — the reserved-key round-trip
   * and the migration script belong to CREATE, the way the workflow's parameters grid keeps
   * every row local until its host saves. Sending each cell through the add path re-sorted the
   * grid, stole the caret and hit the backend on every keystroke.
   */
  updateColumn = (column: IDatabaseColumnModel, patch: Partial<IDatabaseColumnModel>): void => {
    const index: number = this.columnIndex(column);
    if (index === -1) return;
    const previous: IDatabaseColumnModel = this.state.tempData[index];
    const next: IDatabaseColumnModel = { ...previous, ...patch };
    const origin: IDatabaseColumnModel | undefined = this.savedColumnOf(previous);
    if (origin) this.columnOrigins.set(next, origin);
    const tempData: IDatabaseColumnModel[] = [...this.state.tempData];
    tempData[index] = next;
    this.setState(
      { tempData, serverColumnErrors: this.carryServerErrorsTo(previous, next, patch) },
      this.setSubmitButtonDisabledStatus
    );
  };

  /** The saved column a row is, or descends from; undefined for a row added since the table opened. */
  savedColumnOf = (column: IDatabaseColumnModel): IDatabaseColumnModel | undefined => {
    if ((this.props.tableData?.columns ?? []).includes(column)) return column;
    return this.columnOrigins.get(column);
  };

  /** What the server said about a row, moved onto the object an edit replaced it with — minus what the edit answered. */
  carryServerErrorsTo = (
    previous: IDatabaseColumnModel,
    next: IDatabaseColumnModel,
    patch: Partial<IDatabaseColumnModel>
  ): ColumnErrorMap => {
    const current: ColumnErrorMap = this.state.serverColumnErrors;
    const entry: IColumnRowErrors | undefined = current.get(previous);
    if (!entry) return current;
    const carried: ColumnErrorMap = new Map<IDatabaseColumnModel, IColumnRowErrors>(current);
    carried.delete(previous);
    const kept: IColumnRowErrors | undefined = carryServerErrors(entry, editedColumnFields(patch));
    if (kept) carried.set(next, kept);
    return carried;
  };

  /** Marks the rows the server named, and takes the reader to the first of them. */
  markServerColumnErrors = (names: string[], entryFor: (name: string) => IColumnRowErrors): void => {
    if (names.length === 0) return;
    const marked: ColumnErrorMap = new Map<IDatabaseColumnModel, IColumnRowErrors>(this.state.serverColumnErrors);
    names.forEach((name: string): void => {
      const lowered: string = name.toLowerCase();
      const entry: IColumnRowErrors = entryFor(name);
      this.state.tempData
        .filter((row: IDatabaseColumnModel): boolean => (row.name ?? "").toLowerCase() === lowered)
        .forEach((row: IDatabaseColumnModel): void => {
          const current: IColumnRowErrors | undefined = marked.get(row);
          marked.set(row, { ...current, ...entry, fields: { ...current?.fields, ...entry.fields } });
        });
    });
    this.setState({
      serverColumnErrors: marked,
      columnErrorFocusRequest: this.state.columnErrorFocusRequest + 1
    });
  };

  /**
   * What the grid draws: the local checks — once an UPDATE has been refused, then live — over what
   * the server said. Kept while its inputs hold, so the grid is handed the same map between edits.
   */
  getColumnErrors = (): ColumnErrorMap => {
    const { tempData, columnTypes, serverColumnErrors } = this.state;
    const isShown: boolean = this.state.columnErrorFocusRequest > 0;
    const cache: IColumnErrorsCache | undefined = this.columnErrorsCache;
    if (
      cache &&
      cache.tempData === tempData &&
      cache.columnTypes === columnTypes &&
      cache.serverColumnErrors === serverColumnErrors &&
      cache.isShown === isShown
    ) {
      return cache.errors;
    }
    const local: ColumnErrorMap = isShown
      ? validateColumns(tempData ?? [], this.savedColumnOf, columnTypes ?? [])
      : new Map<IDatabaseColumnModel, IColumnRowErrors>();
    const errors: ColumnErrorMap = mergeColumnErrors(local, serverColumnErrors);
    this.columnErrorsCache = { tempData, columnTypes, serverColumnErrors, isShown, errors };
    return errors;
  };

  /** What a column falls back to the moment it stops being nullable. */
  defaultValueForType = (kuikaDataType: string): string | null => {
    if (kuikaDataType === "Boolean") return "true";
    if (kuikaDataType === "DatetimeOffset") return "Now";
    if (kuikaDataType === "Datetime") return "GetDate()";
    if (kuikaDataType === "Guid") return "New Guid";
    if (["Decimal", "Double", "Int64", "Int32", "Int16"].includes(kuikaDataType)) return "0";
    if (kuikaDataType === "String") return "";
    return null;
  };

  /**
   * A name typed into the grid: the three rules the card's name field carried (without them a name
   * with a space reached the migration script), then uniqueness against every other row.
   */
  validateNewColumnName = (name: string, self: IDatabaseColumnModel | undefined): string | null => {
    const error: string | null = validateColumnName(name);
    if (error) return error;
    const lowered: string = name.toLowerCase();
    const isTaken: boolean = this.state.tempData.some(
      (item: IDatabaseColumnModel): boolean => item !== self && item.name.toLowerCase() === lowered
    );
    return isTaken ? DUPLICATE_COLUMN_NAME_MESSAGE : null;
  };

  /** Reports why a rename was refused, so the cell can keep the name and say it; null when taken. */
  handleCellName = (value: string, column: IDatabaseColumnModel): string | null => {
    const error: string | null = this.validateNewColumnName(value, column);
    if (error) {
      KMainFunctions.displayWarningNotification(error);
      return error;
    }
    this.updateColumn(column, { name: value });
    return null;
  };

  handleCellType = (coltype: string, column: IDatabaseColumnModel): void => {
    const colTypeObj = JSON.parse(coltype) as IColumnTypes;
    const kuikaDataType: string = columnTypeName(colTypeObj);
    // Binary is varbinary(MAX) unless the user asks for a width; the card made the same call.
    const maxLength: number = colTypeObj.kuikaTypeName === "Binary" ? -1 : colTypeObj.maxLength;

    this.updateColumn(column, {
      kuikaDataType,
      sqlDataType: colTypeObj.dbTypeName,
      maxLength,
      // A Decimal with no scale writes DECIMAL(p,0); the card started it on 5.
      decimalScale: colTypeObj.kuikaTypeName === "Decimal" ? (colTypeObj.decimalScale ?? "5") : colTypeObj.decimalScale,
      defaultValue: column.nullable ? null : this.defaultValueForType(colTypeObj.kuikaTypeName)
    } as Partial<IDatabaseColumnModel>);
  };

  handleCellSize = (value: number | null, column: IDatabaseColumnModel): void => {
    this.updateColumn(column, { maxLength: value } as Partial<IDatabaseColumnModel>);
  };

  handleCellScale = (value: number | null, column: IDatabaseColumnModel): void => {
    this.updateColumn(column, {
      decimalScale: value == null ? null : value.toString()
    } as Partial<IDatabaseColumnModel>);
  };

  handleCellRequired = (checked: boolean, column: IDatabaseColumnModel): void => {
    this.updateColumn(column, {
      nullable: !checked,
      defaultValue: checked ? this.defaultValueForType(column.kuikaDataType) : null
    } as Partial<IDatabaseColumnModel>);
  };

  handleCellUnique = (checked: boolean, column: IDatabaseColumnModel): void => {
    this.updateColumn(column, { isUnique: checked });
  };

  handleCellDefault = (value: string | number | null, column: IDatabaseColumnModel): void => {
    this.updateColumn(column, {
      defaultValue: value == null ? null : value.toString()
    } as Partial<IDatabaseColumnModel>);
  };

  /**
   * The Default cell. A default only exists on a NOT NULL column, and what it may be depends on
   * the type — the same branches the old columns table drew, moved onto the grid's flush cells.
   */
  renderDefaultCell = (column: IDatabaseColumnModel, onChange: (value: string | number | null) => void): ReactNode => {
    if (column.nullable !== false || column.name === "Id") return "";
    const type: string = column.kuikaDataType;
    const commit = (value: string | number | null): void => onChange(value);

    if (type === "Boolean") {
      return (
        <KuikaSelectbox size="md" value={column.defaultValue} onChange={commit} options={BOOLEAN_DEFAULT_OPTIONS} />
      );
    }
    if (type === "DatetimeOffset") {
      return (
        <KuikaSelectbox
          size="md"
          value={column.defaultValue}
          onChange={commit}
          options={DATETIME_OFFSET_DEFAULT_OPTIONS}
        />
      );
    }
    if (type === "Datetime") {
      return (
        <KuikaSelectbox size="md" value={column.defaultValue} onChange={commit} options={DATETIME_DEFAULT_OPTIONS} />
      );
    }
    if (type === "Guid") return <span className="kuika-create-table__grid__text">{column.defaultValue}</span>;
    if (["Decimal", "Double", "Int64", "Int32", "Int16"].includes(type)) {
      return (
        <KuikaInput
          borderless
          type="number"
          value={column.defaultValue}
          onChange={(_e: unknown, value: string | number): void => commit(value)}
        />
      );
    }
    if (type === "String") {
      return (
        <KuikaInput
          borderless
          value={column.defaultValue}
          onChange={(_e: unknown, value: string | number): void => commit(value)}
        />
      );
    }
    return "";
  };

  // A method, not an inline closure: the grid reports this from an effect keyed on the callback.
  handleDraftPending = (pending: boolean): void => {
    if (this.state.hasPendingColumnDraft !== pending) this.setState({ hasPendingColumnDraft: pending });
  };

  loadTableKeys = async (): Promise<void> => {
    // Whatever was in flight is for the table we just left.
    this.keysAbort?.abort();
    this.keysAbort = null;

    const tableName: string | undefined = this.props.tableData?.name;
    const datasource: IDatasourceDto | undefined = this.props.datasource ?? this.props.sd.creatingTablesDatasource;
    // Managed only: the endpoint refuses anything else, and so does this editor.
    if (!tableName || !datasource || datasource.connectionString !== "") {
      this.setState({ keyData: noKeyData() });
      return;
    }

    const controller: AbortController = new AbortController();
    this.keysAbort = controller;
    try {
      const response = await TableEditorService.QueryRows(
        tableName,
        {
          tableSchema: this.props.tableData?.schema,
          pageNumber: 1,
          pageSize: 1,
          filters: []
        },
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (response?.status !== 200 || !response.data) {
        // No keys is the honest answer here. Returning early would leave the previous table's
        // PK and FK chips standing under this one's name.
        this.setState({ keyData: noKeyData() });
        return;
      }
      this.setState({
        keyData: {
          primaryKeyColumns: response.data.primaryKeyColumns ?? [],
          foreignKeys: response.data.foreignKeys ?? [],
          inboundForeignKeys: response.data.inboundForeignKeys ?? []
        }
      });
    } catch {
      // Keys are chrome: without them the editor reads exactly as it did before. An abort lands
      // here too, and the request that replaced it owns the state from now on.
      if (!controller.signal.aborted) this.setState({ keyData: noKeyData() });
    } finally {
      if (this.keysAbort === controller) this.keysAbort = null;
    }
  };

  /**
   * PK and FK, on the row that edits the column. Both were already on the wire; without them
   * the first news of a missing primary key was Browse Data refusing to open.
   */
  renderColumnKey = (columnName: string, keys: Map<string, ITableColumnKey>): ReactNode => {
    const key: ITableColumnKey | undefined = keys.get((columnName ?? "").toLowerCase());
    if (!key) return null;

    return (
      <span className="kuika-create-table__keys">
        {key.isPk && (
          <span className="kuika-create-table__key kuika-create-table__key--pk" title="Primary key">
            PK
          </span>
        )}
        {key.referencesLabel && (
          <span
            className="kuika-create-table__key kuika-create-table__key--fk"
            title={`References ${key.referencesLabel}`}
          >
            FK
          </span>
        )}
      </span>
    );
  };

  /**
   * A chip list that cannot take over the page: `KUser` is referenced by 41 tables, which drew a
   * 318px band above a 6-row columns table. Six is what a glance takes; the rest is one click.
   */
  renderRelationChips = (labels: ReactNode[]): ReactNode => {
    const isExpanded: boolean = this.state.isRelationsExpanded;
    const shown: ReactNode[] = isExpanded ? labels : labels.slice(0, RELATION_CHIP_LIMIT);
    const hidden: number = labels.length - shown.length;

    return (
      <>
        {shown}
        {hidden > 0 && (
          <button
            type="button"
            className="kuika-create-table__relations-more"
            data-testid="tableRelationsMore"
            onClick={() => this.setState({ isRelationsExpanded: true })}
          >
            {`+${hidden} more`}
          </button>
        )}
        {isExpanded && labels.length > RELATION_CHIP_LIMIT && (
          <button
            type="button"
            className="kuika-create-table__relations-more"
            data-testid="tableRelationsLess"
            onClick={() => this.setState({ isRelationsExpanded: false })}
          >
            Show less
          </button>
        )}
      </>
    );
  };

  /** What this table reaches and what reaches it — the same keys, read table-wide. */
  renderRelations = (): ReactNode => {
    const relations: ITableRelations = buildTableRelations(
      this.state.keyData.foreignKeys,
      this.state.keyData.inboundForeignKeys
    );
    if (relations.references.length === 0 && relations.referencedBy.length === 0) return null;

    return (
      <div className="kuika-create-table__relations" data-testid="tableRelations">
        {relations.references.length > 0 && (
          <div className="kuika-create-table__relations-row">
            <span className="kuika-create-table__relations-label">References</span>
            {this.renderRelationChips(
              relations.references.map((reference: ITableReference) => (
                <span className="kuika-create-table__relation" key={`${reference.column}-${reference.target}`}>
                  <span className="kuika-create-table__relation-column">{reference.column}</span>
                  <span className="kuika-create-table__relation-arrow">→</span>
                  {reference.target}
                </span>
              ))
            )}
          </div>
        )}
        {relations.referencedBy.length > 0 && (
          <div className="kuika-create-table__relations-row">
            <span className="kuika-create-table__relations-label">Referenced by</span>
            {this.renderRelationChips(
              relations.referencedBy.map((label: string) => (
                <span className="kuika-create-table__relation" key={label}>
                  {label}
                </span>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  /**
   * Remove one column. It goes by position rather than by id: the backend hands some columns
   * back with a null id, and `filter(item => item.id !== val.id)` drops every one of those at
   * once — deleting a single column emptied the table.
   */
  deleteColumn = (val: IDatabaseColumnModel): void => {
    const index: number = this.columnIndex(val);
    if (index === -1) return;

    const removed: IDatabaseColumnModel = this.state.tempData[index];
    const tempData: IDatabaseColumnModel[] = this.state.tempData.filter(
      (_item: IDatabaseColumnModel, i: number): boolean => i !== index
    );
    // What the server said about the row goes with it.
    let serverColumnErrors: ColumnErrorMap = this.state.serverColumnErrors;
    if (serverColumnErrors.has(removed)) {
      serverColumnErrors = new Map<IDatabaseColumnModel, IColumnRowErrors>(serverColumnErrors);
      serverColumnErrors.delete(removed);
    }
    this.setState({ tempData, serverColumnErrors }, this.setSubmitButtonDisabledStatus);

    const isPropsExist = _.isEmpty(this.props.tableData);

    if (isPropsExist === false) {
      const isExistInProps: boolean = this.props.tableData.columns.some((item: IDatabaseColumnModel): boolean =>
        removed.id ? item.id === removed.id : item.name === removed.name
      );
      if (isExistInProps) {
        this.setState({ deletedColumns: [...this.state.deletedColumns, removed] });
        this.props.sd.refreshTables();
      }
    }
  };

  /** Which type option a stored column sits on — a table reference by schema and name, on any database. */
  resolveColumnTypeValue = (row: IDatabaseColumnModel): string => {
    const type: IColumnTypes | undefined = resolveColumnType(this.state.columnTypes ?? [], row.kuikaDataType);
    return type ? jsonStringifySafe(type) : "";
  };

  colTypesOnChange = (data: any) => {
    const colTypeObj = JSON.parse(data) as IColumnTypes;
    // Binary defaults to Max (varbinary(MAX)) — file/blob storage is
    // the dominant use case; users who actually want a fixed width
    // (e.g. varbinary(32) for SHA-256) switch the Size selector to
    // Custom and type the number.
    if (colTypeObj.kuikaTypeName === "Binary") {
      const fieldsValue = this.formRef.current?.getFieldsValue();
      if (fieldsValue) {
        fieldsValue.defaultSize = -1;
        this.formRef.current?.setFieldsValue(fieldsValue);
      }
      this.setState({
        currentColType: colTypeObj.kuikaTypeName,
        tableColumnSizeType: ETableColumnSizeType.Max
      });
      return;
    }
    this.setState({ currentColType: colTypeObj.kuikaTypeName });
  };

  deleteSearchText = () => {
    this.setState({ searchText: "" }, () => {
      const fieldsValue = this.formRef.current?.getFieldsValue();
      fieldsValue.colname = "";
      this.formRef.current?.setFieldsValue(fieldsValue);
    });
  };

  onSizeChange = (e: ETableColumnSizeType) => {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.defaultSize = e === ETableColumnSizeType.Max ? -1 : 400;
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ tableColumnSizeType: e });
  };

  setSubmitButtonDisabledStatus() {
    if (!this.formRef.current) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    // A new table can be created as it opens: its generated name and Id column are a table. Read
    // from the state, where the name lives — CREATE confirms a rename still open before it sends.
    if (!this.props.tableData) {
      this.setState({ submitButtonDisabledStatus: !this.state.datasourceName });
      return;
    }

    if (!this.formRef.current.getFieldValue("tableName")) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    if (this.objectHash === this.getObjectHash()) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    this.setState({ submitButtonDisabledStatus: false });
  }

  /**
   * The field sits inside the editor's form: Enter there would also submit it, and Escape would
   * reach whatever listens above the bar. Both stop at the field.
   */
  handleNameInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      e.preventDefault();
      this.confirmRename();
      return;
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      if (this.state.datasourceName) this.handleAbortNameChange();
    }
  };

  /** Renaming usually replaces the name outright — a generated one always — so all of it starts selected. */
  selectNameOnFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    e.currentTarget.select();
  };

  handleAbortNameChange = () => {
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a valid Collection name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      tableName: this.state.datasourceName
    });
    this.setState({ isNameOnEdit: false });
  };

  /**
   * Confirms the typed name, resolving to it — or to `undefined` when a rule refused it, with the
   * reason under the field. The ONLY way a typed name reaches `datasourceName`: a new table's
   * uniqueness and screen/process checks live in these rules alone (the server checks reserved
   * words, not duplicates), so a path that set the name without them would let a clash through.
   */
  handleSubmitNameChange = async (): Promise<string | undefined> => {
    const name: string | undefined = await validateRenameField(this.formRef.current, "tableName");
    if (name === undefined) return undefined;
    this.setState({ isNameOnEdit: false, datasourceName: name });
    return name;
  };

  confirmRename = (): void => {
    void this.handleSubmitNameChange();
  };

  /** The name a save writes: the committed one, or — mid-rename — the typed one once it passes. */
  resolveNameForSave = async (): Promise<string | undefined> => {
    if (this.state.isNameOnEdit) return this.handleSubmitNameChange();
    return this.state.datasourceName;
  };

  /**
   * A new table's name is an identifier that no table, screen or process of the project already
   * holds, whatever the case. A saved table is renamed by the Sources tree's rules, which the
   * Datasources list's rename dialog applies too — one format authority each, so a name is never
   * refused twice in two different words. Keeping its own name is always allowed.
   */
  getTableNameRules = (): Rule[] => {
    const tableData: IDatabaseTableModel | undefined = this.props.tableData;
    if (tableData) {
      return [
        {
          validator: async (_rule: RuleObject, value: unknown): Promise<void> => {
            const name: string = typeof value === "string" ? value : "";
            if (name === tableData.name) return;
            const problem: string | undefined = getTableRenameProblem(
              name,
              getTableRenameNames(this.props.sd, tableData)
            );
            if (problem) throw new Error(problem);
          }
        }
      ];
    }
    return [
      { required: true, message: "Table name is required" },
      { pattern: TABLE_NAME_PATTERN, message: TABLE_NAME_MESSAGE },
      {
        validator: async (_rule: RuleObject, value: unknown): Promise<void> => {
          const problem: string | undefined = validateNewTableName(
            this.props.sd,
            this.props.datasource,
            typeof value === "string" ? value : ""
          );
          if (problem) throw new Error(problem);
        }
      }
    ];
  };

  /**
   * The heading is an inline rename, so the shared header hosts it instead of drawing a title
   * of its own: `schema.name` while idle, the bound field while renaming. Both live inside the
   * form — a field outside its context binds to nothing.
   */
  renderHeaderTitle = (): ReactNode => {
    const schema: string | undefined = this.props.tableData?.schema
      ? this.props.tableData.schema
      : this.props.sd.projects.find((project) => project.id === this.props.sd.project.id)?.schemaName;

    return (
      <KuikaEditorTitle
        entityLabel="table"
        isRenaming={this.state.isNameOnEdit}
        // A Kuika-owned table is read-only: its name is the platform's, not the project's.
        canRename={!this.props.sd.isEditingDefaultKuikaTable}
        // An as-yet-unnamed table has nothing to discard back to.
        canAbortRename={!!this.state.datasourceName}
        onStartRename={() => this.setState({ isNameOnEdit: true })}
        onConfirmRename={this.confirmRename}
        onAbortRename={this.handleAbortNameChange}
        editTestId="tableNameEditButton"
        confirmTestId="tableNameEditTickMark"
        abortTestId="tableNameEditCancelMark"
        identity={
          <TableSwitcherChip
            sd={this.props.sd}
            setNextCallbackFunction={this.props.setNextCallbackFunction}
            datasource={this.props.datasource}
            currentTable={this.props.tableData}
            currentName={`${schema}.${this.state.datasourceName}`}
            onManageAll={() => this.handleCloseDatasource()}
            isDataTabActive={this.state.activeTab === "data"}
            onBrowseCurrent={this.openDataTab}
          />
        }
        renameField={
          <Form.Item name="tableName" rules={this.getTableNameRules()} validateFirst>
            <KuikaInput
              autoFocus
              data-testid="tableNameInput"
              data-onboarding-id="data-diagram_create-table_name-input"
              onKeyDown={this.handleNameInputOnEnter}
              onFocus={this.selectNameOnFocus}
              placeholder="Table name"
            />
          </Form.Item>
        }
      />
    );
  };

  /** Offline support is a property of the table, so it rides in the heading, not the body. */
  renderWorkOfflineSwitch = (): ReactNode => {
    if (KMainFunctions.isMainAI() || !this.isSqlServerWorkspace) return null;

    return (
      <div className="work-offline-switch">
        <span>WORK OFFLINE</span>
        <KuikaSwitch
          data-testid="workOfflineSwitch"
          data-onboarding-id="data-diagram_create-table_work-offline-switch"
          checked={this.state.isWorkOffline}
          onChange={(isWorkOffline) => {
            this.setState({ isWorkOffline });
          }}
        />
      </div>
    );
  };

  handleDataLossConfirm = (): void => {
    this.setState({ isDataLossConfirmOpen: false });
    this.onCreate();
  };

  /**
   * The type picker's tabs: Primitive, dbo, the project's schema, then every other schema. An
   * option's value is the serialised type the grid hands back; its label is what search matches.
   * A table reference is drawn as a table without asking the schema cache — it IS one.
   */
  getColumnTypeCategories = (): IDataTypePanelCategory[] => {
    const columnTypes: IColumnTypes[] = this.state.columnTypes ?? [];
    const projectSchema: string | undefined = this.getProjectSchemaName();
    const cache: ITypeCategoriesCache | undefined = this.typeCategoriesCache;
    if (cache && cache.columnTypes === columnTypes && cache.projectSchema === projectSchema) return cache.categories;

    const categories: IDataTypePanelCategory[] = groupColumnTypes(columnTypes, projectSchema).map(
      (group: IColumnTypeGroup): IDataTypePanelCategory => ({
        key: group.key,
        label: group.label,
        testId: `columnTypeTab-${group.key}`,
        options: group.types.map((type: IColumnTypes): IDataTypePanelOption => ({
          value: jsonStringifySafe(type),
          label: columnTypeName(type),
          icon: isTableReferenceType(type) ? <TableSvg /> : this.getTypeIcon(type.kuikaTypeName),
          testId: "columnTypeOption"
        }))
      })
    );
    this.typeCategoriesCache = { columnTypes, projectSchema, categories };
    return categories;
  };

  /** The schema a new table goes to — the current project's. */
  getProjectSchemaName = (): string | undefined =>
    this.props.sd.projects.find((project) => project.id === this.props.sd.project.id)?.schemaName;

  /** The two ways a table can be taken offline, each stating what it costs. */
  getWorkOfflineOptions = (): KuikaRadioGroupOption[] => [
    {
      value: EWorkOfflineType.WithSchema,
      label: (
        <div className="kuika__data-diagram__work-offline__radio-container">
          <div className="kuika__data-diagram__work-offline__radio-container__section">
            <span
              data-testid="withSchemaRadioButton"
              data-onboarding-id="data-diagram_create-table_with-schema-radio"
              className="kuika__data-diagram__work-offline__radio-label"
            >
              Schema Only
            </span>
          </div>
          <span className="kuika__data-diagram__work-offline__description">
            Creates the table in offline database only with its schema. It does not contain data.
          </span>
        </div>
      )
    },
    {
      value: EWorkOfflineType.WithData,
      label: (
        <div className="kuika__data-diagram__work-offline__radio-container">
          <div className="kuika__data-diagram__work-offline__radio-container__section">
            <span
              data-testid="withDataRadioButton"
              data-onboarding-id="data-diagram_create-table_with-data-radio"
              className="kuika__data-diagram__work-offline__radio-label"
            >
              With data
            </span>
          </div>
          {this.state.workOfflineType === EWorkOfflineType.WithData && !this.props.sd.isEditingDefaultKuikaTable ? (
            <div className="kuika__data-diagram__sync-criteria">
              <div className="kuika__data-diagram__sync-criteria__section">
                <div className="kuika__data-diagram__sync-criteria__header">
                  <span className="kuika__data-diagram__sync-criteria__icon kuika__data-diagram__sync-criteria__icon--download">
                    ↓
                  </span>
                  <span className="kuika__data-diagram__sync-criteria__label">Download filter</span>
                </div>
                <span className="kuika__data-diagram__sync-criteria__hint">
                  Which records should be downloaded from the server to the device?
                </span>
                <Form.Item name="whereCriteria" noStyle>
                  <KuikaInput
                    className="kuika__data-diagram__input"
                    leftUnit={`SELECT * FROM ${this.getTableSchemaName()}.${this.state.datasourceName} WHERE`}
                  />
                </Form.Item>
              </div>

              <div className="kuika__data-diagram__sync-criteria__section">
                <div className="kuika__data-diagram__sync-criteria__header">
                  <span className="kuika__data-diagram__sync-criteria__icon kuika__data-diagram__sync-criteria__icon--upload">
                    ↑
                  </span>
                  <span className="kuika__data-diagram__sync-criteria__label">Upload filter</span>
                </div>
                <span className="kuika__data-diagram__sync-criteria__hint">
                  Which records should be sent from the device back to the server?
                </span>
                <Form.Item name="willSendRecordsCriteria" noStyle>
                  <KuikaInput
                    className="kuika__data-diagram__input"
                    leftUnit={`SELECT * FROM ${this.state.datasourceName} WHERE`}
                  />
                </Form.Item>
              </div>

              <div className="kuika__data-diagram__sync-criteria__params-note">
                Available parameters: <code>@kuikauserid</code> (logged-in user ID), <code>@kuikausername</code>{" "}
                (username)
              </div>

              <div className="kuika__data-diagram__sync-criteria__editing-toggle">
                <KuikaSwitch
                  data-testid="enableEditing"
                  checked={this.state.enableEditing}
                  onChange={(enableEditing) => {
                    this.setState({ enableEditing });
                  }}
                  className="property__switch"
                  title="Allow users to modify records while offline"
                />
                <div className="kuika__data-diagram__sync-criteria__editing-toggle__text">
                  <span className="kuika__data-diagram__sync-criteria__label">Allow offline editing</span>
                  <span className="kuika__data-diagram__sync-criteria__hint">
                    When enabled, downloaded records can be updated on the device and pushed back to the server on the
                    next sync. When disabled, downloaded records are read-only.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <span className="kuika__data-diagram__work-offline__description">
              {this.props.sd.isEditingDefaultKuikaTable
                ? "Creates the table in offline database with both the schema and the data."
                : "Creates the table in offline database with both the schema and the data filtered according to the criteria you specify."}
            </span>
          )}
        </div>
      )
    }
  ];

  /** Dropping a column drops its data, so that one commit asks first. */
  renderSubmitWithDataLossConfirm = (submitButton: ReactNode): ReactNode => {
    if (this.state.deletedColumns.length === 0) return submitButton;

    const confirmBody: ReactNode = (
      <div className="kuika-create-table__data-loss-confirm">
        <KuikaText>Removing a field or an entity might yield data loss. Are you sure you want to continue?</KuikaText>
        <div className="kuika-flex-end--gap">
          <KuikaButton
            type="tertiary"
            color="neutral"
            size="md"
            data-testid="dataLossConfirmCancel"
            onClick={() => this.setState({ isDataLossConfirmOpen: false })}
          >
            NO
          </KuikaButton>
          <KuikaButton
            type="primary"
            color="danger"
            size="md"
            data-testid="dataLossConfirmOk"
            onClick={this.handleDataLossConfirm}
          >
            YES
          </KuikaButton>
        </div>
      </div>
    );

    return (
      <KuikaPopover
        position="bottom"
        align="end"
        visible={this.state.isDataLossConfirmOpen}
        onVisibleChange={(isDataLossConfirmOpen: boolean) => this.setState({ isDataLossConfirmOpen })}
        content={confirmBody}
      >
        {submitButton}
      </KuikaPopover>
    );
  };

  /**
   * Columns | Data, on the bar's midline. Columns carries a dot while the schema is edited; Data carries the
   * count of pending rows, or — where it is off — says why on hover (a `title`: the disabled option's overlay
   * swallows a tooltip).
   */
  renderTabs = (isColumnsDirty: boolean, dataTabDisabledReason: string | undefined): ReactNode => {
    const rowsDirtyCount: number = this.state.rowsPending.isDirty ? this.state.rowsPending.dirtyCount : 0;
    return (
      <KuikaTabs
        size="lg"
        dataTestId="tableEditorTabs"
        className="kuika-create-table__tabs"
        value={this.state.activeTab}
        onChange={this.handleTabChange}
        options={[
          {
            value: "columns",
            dataTestId: "tableEditorColumnsTab",
            label: (
              <span className="kuika-create-table__tab">
                Columns
                {isColumnsDirty && (
                  <span className="kuika-create-table__tab-dot" role="img" aria-label="Unsaved column changes" />
                )}
              </span>
            )
          },
          {
            value: "data",
            dataTestId: "tableEditorDataTab",
            disabled: !!dataTabDisabledReason,
            title: dataTabDisabledReason,
            label: (
              <span className="kuika-create-table__tab">
                Data
                {rowsDirtyCount > 0 && (
                  <span className="kuika-create-table__tab-count">
                    {rowsDirtyCount}
                    <span className="kuika-visually-hidden"> unsaved row changes</span>
                  </span>
                )}
              </span>
            )
          }
        ]}
      />
    );
  };

  /** The embedding, rebuilt only when what it says changes — a new object would re-render the whole grid. */
  getRowsEmbedding = (isActive: boolean, isColumnsDirty: boolean): ITableRowsEditorEmbedding => {
    const notice: ReactNode = isColumnsDirty ? SCHEMA_NOTICE : undefined;
    const current: ITableRowsEditorEmbedding | undefined = this.rowsEmbedding;
    if (current && current.isActive === isActive && current.notice === notice) return current;
    this.rowsEmbedding = { isActive, onPendingChange: this.handleRowsPendingChange, notice };
    return this.rowsEmbedding;
  };

  /** The Data tab: the row browser, embedded, on the table as it is SAVED. */
  renderRowsEditor = (isColumnsDirty: boolean): ReactNode => {
    const table: IDatabaseTableModel | undefined = this.props.tableData;
    if (!table) return null;
    const filter: IInitialFilter | undefined = this.state.dataFilter;
    return (
      <TableRowsEditor
        ref={this.rowsEditorRef}
        // The saved name, never the one being typed into the rename field: the rows are the saved table's. The
        // filter is in the key so an FK jump within the table re-opens it on the referenced row. A missing schema
        // stays empty — not "dbo" — as the row browser's view storage keys it.
        key={`${table.schema ?? ""}.${table.name}.${filter?.column ?? ""}=${filter?.value ?? ""}`}
        tableName={table.name}
        tableSchema={table.schema}
        sqlDialect={resolveTableEditorSqlDialect(this.props.datasource?.datasourceType)}
        datasourceId={this.props.datasource?.id}
        initialFilter={filter}
        onNavigateToTable={this.handleRowsNavigate}
        embedded={this.getRowsEmbedding(this.state.activeTab === "data", isColumnsDirty)}
      />
    );
  };

  render() {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    this.setSubmitButtonDisabledStatus();
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";
    const isColumnsDirty: boolean = this.isColumnsDirty();
    const isDataTab: boolean = this.state.activeTab === "data";
    const workOfflineSwitch: ReactNode = this.renderWorkOfflineSwitch();

    return (
      <KuikaSpinnerSmall spinning={isSaving}>
        <div style={{ width: "100%", height: "100%" }}>
          <Form
            name="basic"
            onFinishFailed={this.onFinishFailed}
            ref={this.formRef}
            onFieldsChange={(changedFields) => {
              const isWhereCriteriaChanged = changedFields.find(({ name }) => name[0] === "whereCriteria");
              if (isWhereCriteriaChanged) {
                this.setSubmitButtonDisabledStatus();
              }
            }}
            initialValues={{
              required: false,
              unique: false,
              defaultBoolean: "true",
              defaultDatetimeOffset: "Now",
              defaultDatetime: "GetDate()",
              defaultDecimal: 5,
              defaultDouble: 0,
              defaultGuid: "New Guid",
              defaultInt64: 0,
              defaultInt32: 0,
              defaultInt16: 0,
              defaultString: "",
              defaultSize: 400
            }}
            layout="vertical"
            style={{ height: "100%", width: "100%" }}
          >
            <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar kuika-create-table__page">
              <KuikaEditorHeader
                title={this.state.datasourceName}
                backLabel="Back to Datasources"
                renderTitle={this.renderHeaderTitle}
                center={this.renderTabs(isColumnsDirty, this.getDataTabDisabledReason())}
                // WORK OFFLINE and UPDATE are the schema's: on Data they step aside (the rows commit in their own
                // strip). Hidden, not unmounted, so the data-loss confirm and the button's status survive a trip.
                extra={
                  workOfflineSwitch && (
                    <span className="kuika-create-table__bar-slot" hidden={isDataTab}>
                      {workOfflineSwitch}
                    </span>
                  )
                }
                submitLabel={this.props.tableData ? "UPDATE" : "CREATE"}
                onSubmit={this.state.deletedColumns.length > 0 ? () => undefined : () => this.onCreate()}
                onCancel={() => this.handleCloseDatasource()}
                status={this.state.saveStatus}
                isSubmitDisabled={this.state.submitButtonDisabledStatus}
                renderSubmit={(submitButton: ReactNode): ReactNode => (
                  <span className="kuika-create-table__bar-slot" hidden={isDataTab}>
                    {this.renderSubmitWithDataLossConfirm(submitButton)}
                  </span>
                )}
                cancelTestId="tableCancelButton"
                submitTestId="tableUpdateCreateButton"
              />
              {/* Both tabs stay mounted: the form reads only the fields that are, and the rows keep their edits. */}
              <KuikaTabContent
                className="kuika-create-table__panes"
                activeKey={this.state.activeTab}
                order={TABLE_EDITOR_TABS}
                keepMounted
              >
                {/* Everything the form owns, on its own card and scrolling under the bar. */}
                <div key="columns" className="kuika-create-table__form-scroll">
                  {this.state.isWorkOffline && (
                    <div className="kuika__data-diagram__work-offline__container">
                      <div
                        data-testid="workOfflineTitle"
                        data-onboarding-id="data-diagram_create-table_work-offline-title"
                        className="kuika__data-diagram__work-offline__title"
                      >
                        WORK OFFLINE
                      </div>
                      <span
                        data-testid="workOfflineDescription"
                        data-onboarding-id="data-diagram_create-table_work-offline-desc"
                        className="kuika__data-diagram__work-offline__description"
                      >
                        This feature allows data tables to work offline.
                      </span>
                      <KuikaRadioGroup
                        className="kuika__data-diagram__work-offline__radio-group"
                        align="top"
                        value={this.state.workOfflineType}
                        onChange={(workOfflineType) =>
                          this.setState({ workOfflineType: workOfflineType as EWorkOfflineType })
                        }
                        options={this.getWorkOfflineOptions()}
                      />
                    </div>
                  )}

                  {/* The page above is the only scroller. A second one here shrank this box to its
                  floor and scrolled the columns table inside it, which took the header row out
                  of view — worst in work-offline mode, where the filters push the page taller. */}
                  <div
                    className="kuika__data-diagram__create-con__container__item"
                    style={{ height: "auto", minHeight: 320, flexShrink: 0, margin: 0, padding: 24 }}
                  >
                    <TableColumnsGrid
                      // Insertion order, not alphabetical: a row that jumps somewhere else the moment it
                      // is named takes the edit that was just made out from under the caret.
                      columns={this.state.tempData?.filter(
                        (x) =>
                          x.name
                            .toLowerCase()
                            .indexOf(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()) !== -1
                      )}
                      typeCategories={this.getColumnTypeCategories()}
                      renderKeys={(columnName: string) =>
                        this.renderColumnKey(
                          columnName,
                          buildColumnKeys(
                            this.state.tempData ?? [],
                            this.state.keyData.foreignKeys,
                            this.state.keyData.primaryKeyColumns
                          )
                        )
                      }
                      renderTypeLabel={(column) => (
                        <>
                          {this.getTypeIcon(column.kuikaDataType)}
                          <span>{column.kuikaDataType}</span>
                        </>
                      )}
                      renderNameSuffix={(columnName: string) => {
                        const key: ITableColumnKey | undefined = buildColumnKeys(
                          this.state.tempData ?? [],
                          this.state.keyData.foreignKeys,
                          this.state.keyData.primaryKeyColumns
                        ).get((columnName ?? "").toLowerCase());
                        if (!key?.referencesLabel) return null;
                        // Titled because the tail still ellipsises in a cell that is genuinely too narrow.
                        return (
                          <span className="kuika-create-table__grid__ref" title={`References ${key.referencesLabel}`}>
                            {`→ ${key.referencesLabel}`}
                          </span>
                        );
                      }}
                      renderDefault={this.renderDefaultCell}
                      defaultForType={this.defaultValueForType}
                      resolveTypeValue={this.resolveColumnTypeValue}
                      edit={{
                        onName: this.handleCellName,
                        onType: this.handleCellType,
                        onSize: this.handleCellSize,
                        onScale: this.handleCellScale,
                        onRequired: this.handleCellRequired,
                        onUnique: this.handleCellUnique,
                        onDefault: this.handleCellDefault
                      }}
                      onAddColumn={this.handleAddColumn}
                      onDraftPending={this.handleDraftPending}
                      onProposeType={this.proposeColumnType}
                      onDelete={this.deleteColumn}
                      readOnly={this.props.sd.isEditingDefaultKuikaTable}
                      aiAvailable={!KMainFunctions.isMainAI()}
                      aiActive={this.state.shouldGenerateColumnWithAI}
                      aiBusy={this.state.isColumnGeneratingWithAI}
                      onToggleAi={() =>
                        this.setState({ shouldGenerateColumnWithAI: !this.state.shouldGenerateColumnWithAI }, () => {
                          this.prevNewColName = "";
                        })
                      }
                      errors={this.getColumnErrors()}
                      draftError={
                        this.state.columnErrorFocusRequest > 0 && this.state.hasPendingColumnDraft
                          ? DRAFT_PENDING_MESSAGE
                          : undefined
                      }
                      errorFocusRequest={this.state.columnErrorFocusRequest}
                    />
                    {/* Outside the table wrapper: that wrapper takes its height from the table alone,
                    so anything nested after it overflows the scroll box and loses its own margin. */}
                    {this.renderRelations()}
                  </div>
                </div>
                {/* Mounted on the first visit, kept after it. */}
                <div key="data" className="kuika-create-table__data">
                  {this.state.hasOpenedData && this.renderRowsEditor(isColumnsDirty)}
                </div>
              </KuikaTabContent>
            </div>
          </Form>
          <CloseConfirmationModal
            itemName={this.state.datasourceName}
            closeType={"Table"}
            triggerModal={this.props.showModal}
            isLoading={isSaving || this.state.rowsPending.isSaving}
            updateAndCloseHandler={this.saveAndClose}
            closeWithoutSaveHandler={() => {
              this.props.setShowModal(false);
              this.closeDataSource();
            }}
            cancel={() => {
              this.props.setNextCallbackFunction();
              this.props.setShowModal(false);
            }}
          />
        </div>
      </KuikaSpinnerSmall>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createTable = connect(mapStateToProps, mapDispatchToProps)(withSD<ICreateTableProps>(CreateTable));
export { createTable as CreateTable };
